import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Link, LangRedirect } from "@/components/LangLink";
import { Check, ChevronLeft, AlertTriangle, Info, ArrowRight, Ruler, CalendarCheck } from "lucide-react";
import { CTASection } from "@/components/sections";
import { Reveal } from "@/components/primitives";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ProductGallery, ColorSelector, ExecutionSelector, OptionSelector, RequestInfoModal, CompressionGuide, CompressionClassSelector } from "@/components/product-detail";
import { SizeGuide } from "@/components/size-guide";
import { RalTechBlock, RalBadge } from "@/components/quality";
import { getSchieblerProduct, resolveExecutions } from "@/lib/schiebler";
import { useAssistant } from "@/contexts/AssistantContext";
import { schieblerContext } from "@/lib/productContext";

const MaterialTable = ({ rows }) => (
  <dl className="overflow-hidden rounded-xl border border-[#E7EDF2]">
    {rows.map((m, i) => (
      <div key={m[0]} className={`flex justify-between gap-4 px-4 py-2.5 ${i % 2 === 0 ? "bg-cloud" : "bg-white"}`}>
        <dt className="text-sm text-navy/60">{m[0]}</dt>
        <dd className="text-sm font-medium text-navy text-right">{m[1]}</dd>
      </div>
    ))}
  </dl>
);
const Bullets = ({ items }) => (
  <ul className="space-y-2">
    {items.map((v) => (<li key={v} className="flex items-start gap-2.5 text-sm text-navy/80"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green" />{v}</li>))}
  </ul>
);

const SchieblerProduct = () => {
  const { slug } = useParams();
  const product = getSchieblerProduct(slug);

  const [color, setColor] = useState(null);
  const [classe, setClasse] = useState(null);
  const [taglia, setTaglia] = useState(null);
  const [lato, setLato] = useState(null);
  const [foot, setFoot] = useState(null);
  const [confezione, setConfezione] = useState(null);
  const [execution, setExecution] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const hasClasses = product?.classes?.length > 0;
  const currentClass = useMemo(() => {
    if (!hasClasses) return null;
    return product.classes.find((c) => c.id === classe) || product.classes[0];
  }, [product, classe, hasClasses]);

  const { setProductCtx } = useAssistant();
  useEffect(() => {
    if (product) setProductCtx(schieblerContext(product));
    return () => setProductCtx(null);
  }, [product, setProductCtx]);

  if (!product) return <LangRedirect to="/prodotti/schiebler" />;

  const colorId = color || product.colors?.[0]?.id;
  const selectedColorName = product.colors?.find((c) => c.id === colorId)?.name || product.colors?.[0]?.name;

  const executions = resolveExecutions(product.availableExecutions);
  const currentExecution = execution || executions[0]?.id;
  const execLabel = executions.find((e) => e.id === currentExecution)?.label;
  const hasSizeGuide = (product.sizeGuide || product.madeToMeasure) && executions.length > 0;

  // Galleria: foto filtrate per colore, con priorità alle foto specifiche dell'esecuzione.
  const photos = product.photos || [];
  const photoPool = photos.filter((p) => p.color === colorId);
  const execPhotos = photoPool.filter((p) => p.execution === currentExecution);
  const galleryImages = (execPhotos.length ? execPhotos : photoPool).map((p) => p.src);

  const summary = {
    brand: "SCHIEBLER",
    product: product.name,
    color: product.colors?.length ? selectedColorName : undefined,
    execution: execLabel || undefined,
    classe: currentClass ? `Classe ${Number(currentClass.id.replace(/\D/g, ""))}` : undefined,
    taglia: product.sizes?.length ? (taglia || undefined) : undefined,
    lato: product.side?.length ? (lato || undefined) : undefined,
    confezione: product.packages?.length ? (confezione || undefined) : undefined,
  };
  if (product.footLength?.length && foot) summary.taglia = `${summary.taglia || ""} · Piede ${foot}`.trim();

  return (
    <>
      <div className="border-b border-[#E7EDF2] bg-white pt-[92px] lg:pt-[104px]">
        <div className="container-x flex items-center gap-2 py-4 text-sm">
          <Link to="/prodotti/schiebler" data-testid="product-breadcrumb-back" className="inline-flex items-center gap-1 text-navy/60 transition-colors hover:text-green">
            <ChevronLeft size={15} /> SCHIEBLER
          </Link>
          <span className="text-navy/30">/</span>
          <span className="text-navy/80">{product.name}</span>
        </div>
      </div>

      <section className="py-12 lg:py-16 bg-white" data-testid={`schiebler-product-${product.slug}`}>
        <div className="container-x grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal>
              <ProductGallery key={`${colorId}-${currentExecution}`} images={galleryImages} label={product.colors?.length ? `${selectedColorName} · foto in arrivo` : "Foto in arrivo"} testid="product-gallery" />
            </Reveal>
            {(product.colors?.length > 0 || product.colorsNote) && (
              <div className="mt-6">
                <ColorSelector colors={product.colors} note={product.colorsNote} value={colorId} onChange={setColor} />
                {product.colors?.length > 0 && (
                  <p className="mt-3 text-xs text-muted-foreground" data-testid="color-disclaimer">
                    I colori visualizzati sono indicativi e possono differire dal prodotto reale.
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            {product.featured && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green/12 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-green-deep" data-testid="product-most-chosen">
                <span className="h-1.5 w-1.5 rounded-full bg-green" /> Tra i più scelti
              </span>
            )}
            <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-green-deep">{product.category}</div>
            <div className="mt-1.5 flex flex-wrap items-center gap-3">
              <h1 className="font-serif text-4xl lg:text-5xl font-bold text-navy">{product.name}</h1>
              {product.ralCertified && <RalBadge />}
            </div>
            <p className="mt-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">Marchio: SCHIEBLER</p>
            {product.madeToMeasure && (
              <span data-testid="made-to-measure-badge" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-navy/5 px-3 py-1.5 text-xs font-semibold text-navy">
                <Ruler size={13} className="text-green" /> Prodotto realizzato su misura
              </span>
            )}
            <p className="mt-4 text-lg text-navy/80">{product.title}</p>
            {product.pressure && (
              <span className="mt-4 inline-flex items-center rounded-full bg-navy/5 px-3 py-1.5 text-xs font-semibold text-navy">Compressione {product.pressure}</span>
            )}

            <ul className="mt-6 space-y-2.5">
              {product.features?.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-navy/80">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green/12 text-green"><Check size={12} strokeWidth={3} /></span>
                  {f}
                </li>
              ))}
            </ul>

            <div className="mt-8 space-y-6">
              {hasClasses && <CompressionClassSelector product={product} value={classe} onChange={setClasse} />}
              {executions.length > 0 && <ExecutionSelector executions={executions} value={currentExecution} onChange={setExecution} />}
              {product.sizes?.length > 0 && <OptionSelector label="Taglia" testid="taglia" options={product.sizes} value={taglia} onChange={setTaglia} />}
              {product.footLength?.length > 0 && <OptionSelector label="Lunghezza piede" testid="piede" options={product.footLength} value={foot} onChange={setFoot} />}
              {product.side?.length > 0 && <OptionSelector label="Lato" testid="lato" options={product.side} value={lato} onChange={setLato} />}
              {product.packages?.length > 0 && <OptionSelector label="Confezione" testid="confezione" options={product.packages} value={confezione} onChange={setConfezione} />}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button type="button" onClick={() => setModalOpen(true)} data-testid="request-info-open" className="btn-green w-full sm:w-auto">
                Richiedi informazioni <ArrowRight size={16} />
              </button>
              {product.madeToMeasure && executions.length === 0 && (
                <Link to="/contatti#prenota" data-testid="request-book-measurement" className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-semibold text-navy transition-colors hover:border-green hover:text-green">
                  <CalendarCheck size={16} /> Prenota una misurazione
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="container-x mt-14 max-w-3xl">
          <h2 className="font-serif text-2xl font-bold text-navy">Descrizione</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed" data-testid="product-description">{product.description}</p>
          {product.materialNote && (
            <p className="mt-4 flex items-start gap-2 rounded-xl border border-green/20 bg-green/5 px-4 py-3 text-sm text-navy/80">
              <Info size={16} className="mt-0.5 shrink-0 text-green" /> {product.materialNote}
            </p>
          )}

          <Accordion type="multiple" className="mt-8" data-testid="product-panels">
            {!hasClasses && product.material && (
              <AccordionItem value="materiale">
                <AccordionTrigger data-testid="panel-materiale" className="text-navy">Composizione / Materiale</AccordionTrigger>
                <AccordionContent><MaterialTable rows={product.material} /></AccordionContent>
              </AccordionItem>
            )}
            {!hasClasses && product.variants?.length > 0 && (
              <AccordionItem value="varianti">
                <AccordionTrigger data-testid="panel-varianti" className="text-navy">Caratteristiche e varianti</AccordionTrigger>
                <AccordionContent><Bullets items={product.variants} /></AccordionContent>
              </AccordionItem>
            )}
            {!hasClasses && product.indications?.length > 0 && (
              <AccordionItem value="indicazioni">
                <AccordionTrigger data-testid="panel-indicazioni" className="text-navy">Indicazioni</AccordionTrigger>
                <AccordionContent><Bullets items={product.indications} /></AccordionContent>
              </AccordionItem>
            )}
            {product.ingredients?.length > 0 && (
              <AccordionItem value="ingredienti">
                <AccordionTrigger data-testid="panel-ingredienti" className="text-navy">Ingredienti</AccordionTrigger>
                <AccordionContent>
                  <Bullets items={product.ingredients} />
                  {product.ingredientsNote && <p className="mt-3 text-xs italic text-muted-foreground">{product.ingredientsNote}</p>}
                </AccordionContent>
              </AccordionItem>
            )}
            {hasClasses && <CompressionGuide />}
            {hasSizeGuide && <SizeGuide product={product} execution={currentExecution} onAskSize={() => setModalOpen(true)} />}
          </Accordion>

          {/* Marchio RAL — solo prodotti certificati (ralCertified: true) */}
          {product.ralCertified && <RalTechBlock />}

          {product.safetyBox && (
            <div className="mt-8 flex items-start gap-3 rounded-2xl border-2 border-amber-400 bg-amber-50 px-5 py-4" data-testid="product-safety-box">
              <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-600" />
              <div>
                <div className="text-sm font-bold text-amber-800">Avvertenze di sicurezza</div>
                <p className="mt-1 text-sm text-amber-900">{product.safetyBox}</p>
              </div>
            </div>
          )}

          {product.device && product.healthNote && (
            <p className="mt-8 text-xs text-muted-foreground leading-relaxed" data-testid="product-health-note">{product.healthNote}</p>
          )}

          {product.faq?.length > 0 && (
            <div className="mt-12" data-testid="product-faq">
              <h2 className="font-serif text-2xl font-bold text-navy">Domande frequenti</h2>
              <Accordion type="single" collapsible className="mt-6">
                {product.faq.map(([q, a], i) => (
                  <AccordionItem key={q} value={`faq-${i}`}>
                    <AccordionTrigger data-testid={`faq-${i}`} className="text-left text-navy">{q}</AccordionTrigger>
                    <AccordionContent><p className="text-sm text-navy/80 leading-relaxed">{a}</p></AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
      </section>

      <RequestInfoModal open={modalOpen} onOpenChange={setModalOpen} summary={summary} />

      <CTASection />
    </>
  );
};

export default SchieblerProduct;
